import type { Vector2d } from "konva/lib/types";

interface SizedRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const FIT_PADDING = 60; // px of breathing room around the content
const MIN_SCALE = 0.1;
const MAX_SCALE = 2;

/** Axis-aligned bounds of all rects, or null for an empty canvas. */
export const contentBounds = (
  rects: ReadonlyArray<SizedRect>
): { minX: number; minY: number; maxX: number; maxY: number } | null => {
  if (rects.length === 0) return null;
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const r of rects) {
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.width);
    maxY = Math.max(maxY, r.y + r.height);
  }
  return { minX, minY, maxX, maxY };
};

/**
 * Scale + stage position that brings every rect into a viewport of the
 * given size, centered, clamped to the zoom limits the toolbar allows.
 */
export const fitTransform = (
  rects: ReadonlyArray<SizedRect>,
  viewport: ViewportRect
): { scale: number; position: Vector2d } | null => {
  const b = contentBounds(rects);
  if (!b) return null;
  const w = b.maxX - b.minX + FIT_PADDING * 2;
  const h = b.maxY - b.minY + FIT_PADDING * 2;
  const raw = Math.min(viewport.width / w, viewport.height / h);
  const scale =
    Math.round(Math.min(MAX_SCALE, Math.max(MIN_SCALE, raw)) * 100) / 100;
  // Center the content box in the VISIBLE viewport at the chosen scale.
  const position = {
    x:
      viewport.left +
      (viewport.width - (b.maxX - b.minX) * scale) / 2 -
      b.minX * scale,
    y:
      viewport.top +
      (viewport.height - (b.maxY - b.minY) * scale) / 2 -
      b.minY * scale,
  };
  return { scale, position };
};

/** Rect indices entirely outside the visible viewport. */
export const offscreenIndices = (
  rects: ReadonlyArray<SizedRect>,
  scale: number,
  stagePosition: Vector2d,
  viewport: ViewportRect
): number[] => {
  const out: number[] = [];
  const vRight = viewport.left + viewport.width;
  const vBottom = viewport.top + viewport.height;
  for (let i = 0; i < rects.length; i++) {
    const r = rects[i];
    if (!r) continue;
    const left = r.x * scale + stagePosition.x;
    const top = r.y * scale + stagePosition.y;
    const right = left + r.width * scale;
    const bottom = top + r.height * scale;
    if (
      right < viewport.left ||
      bottom < viewport.top ||
      left > vRight ||
      top > vBottom
    ) {
      out.push(i);
    }
  }
  return out;
};

export interface ViewportRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * The VISIBLE canvas viewport, in stage-container coordinates.
 *
 * The container spans the whole window; the sidebar and navbars are
 * overlays on top of it, so its own box overstates what the user can
 * see (fit-to-content would center nodes underneath the sidebar).
 * Rather than hard-coding the overlay layout, sample elementFromPoint
 * along the container's midlines to find where the canvas actually
 * starts being the topmost element.
 */
export const visibleCanvasViewport = (): ViewportRect => {
  const el = document.querySelector<HTMLElement>(".argument-canvas-container");
  if (!el) {
    return {
      left: 0,
      top: 0,
      width: window.innerWidth * 0.75,
      height: window.innerHeight,
    };
  }
  const rect = el.getBoundingClientRect();
  const isOurs = (x: number, y: number) => {
    const hit = document.elementFromPoint(x, y);
    return hit !== null && el.contains(hit);
  };
  const midY = rect.top + rect.height / 2;
  let left = rect.left;
  for (let x = rect.left + 2; x < rect.right; x += 40) {
    if (isOurs(x, midY)) {
      left = x;
      break;
    }
  }
  const midX = (left + rect.right) / 2;
  let top = rect.top;
  for (let y = rect.top + 2; y < rect.bottom; y += 20) {
    if (isOurs(midX, y)) {
      top = y;
      break;
    }
  }
  return {
    left: left - rect.left,
    top: top - rect.top,
    width: rect.right - left,
    height: rect.bottom - top,
  };
};
